import axios from 'axios';
import { DailyReport } from '../types/mundus';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Generate a daily report for a Mundus
 */
export const generateReport = async (mundusId: string): Promise<DailyReport> => {
  const response = await api.post(`/reports/${mundusId}/generate`);
  return response.data.data;
};

/**
 * Get reports for a Mundus
 */
export const getMundusReports = async (
  mundusId: string,
  limit?: number
): Promise<DailyReport[]> => {
  const response = await api.get(`/reports/${mundusId}`, {
    params: { limit },
  });
  return response.data.data;
};

/**
 * Get a specific report by ID
 */
export const getReportById = async (reportId: string): Promise<DailyReport> => {
  const response = await api.get(`/reports/report/${reportId}`);
  return response.data.data;
};

/**
 * Get today's report for a Mundus (if it exists)
 */
export const getTodaysReport = async (mundusId: string): Promise<DailyReport | null> => {
  try {
    const reports = await getMundusReports(mundusId, 1);
    if (reports.length === 0) return null;

    const latestReport = reports[0];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const reportDate = new Date(latestReport.reportDate);
    reportDate.setHours(0, 0, 0, 0);

    // Check if the latest report is from today
    if (reportDate.getTime() === today.getTime()) {
      return latestReport;
    }

    return null;
  } catch (error) {
    console.error('Error fetching today\'s report:', error);
    return null;
  }
};

/**
 * Submit feedback for a report
 */
export const submitReportFeedback = async (
  reportId: string,
  rating: number,
  feedback?: string
): Promise<DailyReport> => {
  const response = await api.patch(`/reports/report/${reportId}/feedback`, {
    rating,
    feedback,
  });
  return response.data.data;
};

/**
 * Mark a report as read
 */
export const markReportAsRead = async (reportId: string): Promise<DailyReport> => {
  const response = await api.patch(`/reports/report/${reportId}/read`);
  return response.data.data;
};
